<template>
  <article
    class="stage-teaser mb-[40px] grid h-[500px] grid-cols-2 mobile:mb-[20px] mobile:h-auto mobile:grid-cols-1"
    :class="`kik-${position}`"
    :data-ab-id="dataAbId"
  >
    <section
      class="stage-teaser__content flex flex-col justify-end !py-[30px] pb-[80px] mobile:w-full mobile:px-4 tablet:px-4 desktop:px-[40px] "
      :class="`kik-${assetPosition}`"
    >
      <div class="p-0 desktop:pb-[40px]">
        <div class="stage-teaser__top-title uppercase mobile:hidden">
          {{ markupTag }}
        </div>
        <component
          :is="headingTag"
          v-if="title"
          class="stage-teaser__title flex gap-1"
        >
          {{ title }}
          <KikLegalInformationDrawer
            v-if="enableLegalDisclaimer && titleOrSubtitle === 'title'"
            :legal-text="legalText"
            :title="legalTitle"
            class="self-end"
          />
        </component>
        <div class="stage-teaser__subtitle flex gap-1">
          {{ subtitle }}
          <KikLegalInformationDrawer
            v-if="enableLegalDisclaimer && titleOrSubtitle === 'subtitle'"
            :legal-text="legalText"
            :title="legalTitle"
            class="self-end"
          />
        </div>
        <template v-if="link && ctaText">
          <SharedLink
            :link="link"
          >
            {{ ctaText }}
          </SharedLink>
        </template>
      </div>
    </section>
    <video
      v-if="asset.type === 'media'"
      preload="metadata"
      class="w-full object-cover desktop:h-[500px]"
      :muted="asset.muted"
      :autoplay="asset.enableAutoplay"
      controls
      :controlsList="asset.controlsList"
      :disablePictureInPicture="asset.disablePicInPic"
      :playsinline="asset.inlineOnly"
      :poster="asset.poster"
    >
      <source
        :src="asset.src"
        type="video/mp4"
      >
    </video>
    <SharedLink
      v-if="asset.type === 'image'"
      :link="asset.link"
      class="overflow-hidden"
    >
      <NuxtImg
        :src="asset.src"
        :alt="asset.alt"
        :title="asset.title"
        sizes="lg:100vw 48vw"
        :srcset="asset.srcset"
        :width="asset.dimensions.width"
        :height="asset.dimensions.height"
        :loading="imageLoadType"
        :preload="imageLoadType === 'eager'"
        :fetchpriority="imageLoadType === 'eager' ? 'high' : 'low'"
        class="w-full object-cover desktop:h-[500px]"
      />
    </SharedLink>
  </article>
</template>

<script lang="ts" setup>
import magnoliaGetters from '~/composables/getters/magnoliaGetters';

interface Props {
  assetMode?: any;
  assetPosition?: string;
  markupTag?: string;
  markupTagColor?: string;
  title?: string;
  titleColor?: string;
  subtitle?: string;
  subtitleColor?: string;
  backgroundColor?: string;
  headingTag?: string;
  position?: string;
  imageLoadType?: HTMLImageElement['loading'];
  ctaLink?: any[];
  ctaTextColor?: string;
  buttonColor?: string;
  borderColor?: string;
  dataAbId?: string;
  enableLegalDisclaimer?: boolean;
  legalText?: string;
  legalTitle?: string;
  titleOrSubtitle?: string;
  intLinkParam?: any;
}
const props = withDefaults(defineProps<Props>(), {
  assetMode: () => ({}),
  assetPosition: 'right',
  markupTag: '',
  markupTagColor: '',
  title: '',
  titleColor: '',
  alt: '',
  subtitle: '',
  subtitleColor: '',
  backgroundColor: '',
  headingTag: 'h1',
  position: 'bottom-left',
  imageLoadType: 'lazy',
  ctaLink: () => [],
  ctaTextColor: '',
  buttonColor: '',
  borderColor: '',
  dataAbId: '',
  enableLegalDisclaimer: false,
  legalText: '',
  legalTitle: '',
  titleOrSubtitle: 'title',
  intLinkParam: undefined,
});

const {
  assetMode, assetPosition, backgroundColor, ctaLink, markupTagColor, titleColor, subtitleColor, ctaTextColor, buttonColor, borderColor,
} = toRefs(props);
const { $domain } = useNuxtApp();
const { isMobile } = useDevice();

const link = computed(() => (ctaLink.value?.[0] ? magnoliaGetters.getLinkAndName(ctaLink.value[0], $domain.cmsAssetDomainPath, ctaLink.value[0].switchable?.intLinkParam) : {}));
const ctaText = computed(() => (ctaLink.value?.[0] ? ctaLink.value?.[0].ctaText : ''));

const asset = computed(() => {
  if (assetMode.value.field === 'image') {
    return {
      type: assetMode.value.field,
      src: magnoliaGetters.getDefaultSrcAsset(assetMode.value.image, $domain.cmsAssetDomainPath),
      srcset: magnoliaGetters.getSrcSetImagesAsString(assetMode.value.image, $domain.cmsAssetDomainPath, assetMode.value.aspectRatio, assetMode.value.mobileAspectRatio?.mobileAspectRatio0?.aspectRatio, assetMode.value.mobileImage?.mobileImage0?.image),
      link: assetMode.value.imageLink?.imageLink0 ? magnoliaGetters.getLinkAndName(assetMode.value.imageLink.imageLink0, $domain.cmsAssetDomainPath, assetMode.value.imageLink.imageLink0.switchable?.intLinkParam) : {},
      title: magnoliaGetters.getTitleImage(assetMode.value.image, assetMode.value.imageTitle),
      alt: magnoliaGetters.getAltImage(assetMode.value.image, assetMode.value.alt),
      dimensions: magnoliaGetters.getImageDimensions(assetMode.value.image),
    };
  }
  if (assetMode.value.field === 'media') {
    const deviceMedia = isMobile && assetMode.value.mobileMedia?.[0] ? assetMode.value.mobileMedia[0].media : assetMode.value.media;
    return {
      type: assetMode.value.field,
      src: `${magnoliaGetters.getDefaultSrcAsset(deviceMedia, $domain.cmsAssetDomainPath)}#t=${assetMode.value.playback}`,
      muted: assetMode.value.muted,
      controlsList: magnoliaGetters.getControlsList(assetMode.value),
      disablePicInPic: assetMode.value.disablePicInPic,
      inlineOnly: assetMode.value.inlineOnly,
      enableAutoplay: assetMode.value.enableAutoplay,
      poster: assetMode.value.thumbnail,
    };
  }
  return {};
});

const videoRef = ref();

onMounted(() => {
  if (videoRef.value) {
    videoRef.value.addEventListener('loadedmetadata', () => {
      videoRef.value.currentTime = props.starttime;
    });
  }
});
onUnmounted(() => {
  if (videoRef.value) {
    videoRef.value.removeEventListener('loadedmetadata', () => {
      videoRef.value.currentTime = props.starttime;
    });
  }
});
</script>

<style lang="scss" scoped>
.stage-teaser {
  @include for-mobile {
    &.kik-bottom-left {
      display: flex;
      flex-direction: column-reverse;
      width: 100%;

      .stage-teaser__content {
        text-align: left;
        width: 100%;
      }
    }

    &.kik-bottom-right {
      display: flex;
      flex-direction: column-reverse;

      .stage-teaser__content {
        text-align: right;
      }
    }

    &.kik-up-left {
      .stage-teaser__content {
        text-align: left;
      }
    }

    &.kik-up-right {
      .stage-teaser__content {
        text-align: right;
      }
    }
  }

  &__content {
    background: v-bind(backgroundColor);

    &.kik-left {
      align-items: flex-end;
      text-align: right;
      grid-column: 2;
      grid-row: 1;
    }

    &.kik-right {
      grid-column: 1;
      grid-row: 1;
    }

    :deep(a) {
      color: v-bind(ctaTextColor);
      background: v-bind(buttonColor);
      border: 1px solid v-bind(borderColor);
      border-radius: 3px;
      text-decoration: none;
      display: inline-block;
      padding: 10px 30px;
      margin-top: 25px;

      &:hover {
        cursor: pointer;
      }
    }
  }

  &__top-title {
    color: v-bind(markupTagColor);
  }

  &__title {
    color: v-bind(titleColor);
  }

  &__subtitle {
    color: v-bind(subtitleColor);
  }
}
</style>
